// src/components/widgets/FormFieldsTab.jsx
import React, { useState } from 'react';
import { Phone, MessageSquare, HelpCircle, MessageCircle, User, Mail, MapPin } from 'lucide-react';

// shadcn components
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { Card, CardContent } from '../ui/card';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';

// Import existing custom questions tab
import CustomQuestionsTab from './CustomQuestionsTab';

import { cn } from '../../utils/cn';

const FormFieldsTab = ({
  requirePhone,
  requireNotes,
  showHowHeard,
  showSmsConsent,
  onRequirePhoneChange,
  onRequireNotesChange,
  onShowHowHeardChange,
  onShowSmsConsentChange,
  fieldLabels = {},
  onFieldLabelsChange,
  customQuestions = [],
  onCustomQuestionsChange,
  className = ''
}) => {
  // Default values for placeholders
  const defaults = {
    address_label: 'Service Address',
    address_placeholder: 'Enter your service address...',
    name_label: 'Full Name',
    name_placeholder: 'Enter your full name',
    email_label: 'Email Address',
    email_placeholder: 'Enter your email',
    phone_label: 'Phone Number',
    phone_placeholder: '(555) 123-4567',
    notes_label: 'Additional Notes',
    notes_placeholder: 'Any special instructions or details...',
    how_heard_label: 'How did you hear about us?'
  };

  const handleLabelChange = (field, value) => {
    onFieldLabelsChange({
      ...fieldLabels,
      [field]: value
    });
  };

  // Field configurations with their toggle states
  const standardFields = [
    {
      id: 'address',
      label: 'Service Address',
      description: 'Where the service will be performed',
      icon: MapPin,
      alwaysShown: true, // Core field that's always visible
      hasLabel: true,
      hasPlaceholder: true,
      labelKey: 'address_label',
      placeholderKey: 'address_placeholder',
    },
    {
      id: 'name',
      label: 'Customer Name',
      description: 'Full name of the customer',
      icon: User,
      alwaysShown: true, // Core field that's always visible
      hasLabel: true,
      hasPlaceholder: true,
      labelKey: 'name_label',
      placeholderKey: 'name_placeholder',
    },
    {
      id: 'email',
      label: 'Email Address',
      description: 'Customer email for confirmation',
      icon: Mail,
      alwaysShown: true, // Core field that's always visible
      hasLabel: true,
      hasPlaceholder: true,
      labelKey: 'email_label',
      placeholderKey: 'email_placeholder',
    },
    {
      id: 'phone',
      label: 'Phone Number',
      description: 'Customer phone number for appointment confirmation',
      icon: Phone,
      value: requirePhone,
      onChange: onRequirePhoneChange,
      type: 'require',
      recommended: true,
      hasLabel: true,
      hasPlaceholder: true,
      labelKey: 'phone_label',
      placeholderKey: 'phone_placeholder',
    },
    {
      id: 'notes',
      label: 'Notes/Comments',
      description: 'Allow customers to add notes about their appointment',
      icon: MessageSquare,
      value: requireNotes,
      onChange: onRequireNotesChange,
      type: 'require',
      recommended: false,
      hasLabel: true,
      hasPlaceholder: true,
      labelKey: 'notes_label',
      placeholderKey: 'notes_placeholder',
    },
    {
      id: 'how_heard',
      label: 'How did you hear about us?',
      description: 'Marketing field to track customer acquisition',
      icon: HelpCircle,
      value: showHowHeard,
      onChange: onShowHowHeardChange,
      type: 'show',
      recommended: false,
      hasLabel: true,
      hasPlaceholder: false,
      labelKey: 'how_heard_label',
    },
    {
      id: 'sms_consent',
      label: 'SMS Notifications',
      description: 'Allow customers to opt-in to SMS updates',
      icon: MessageCircle,
      value: showSmsConsent,
      onChange: onShowSmsConsentChange,
      type: 'show',
      recommended: true,
      hasLabel: false, // SMS consent typically has fixed text
      hasPlaceholder: false,
    }
  ];

  const renderField = (field) => {
    const Icon = field.icon;
    const isActive = field.alwaysShown ? true : field.value;
    const toggleText = field.alwaysShown
      ? 'Required'
      : field.type === 'require'
        ? (isActive ? 'Required' : 'Optional')
        : (isActive ? 'Shown' : 'Hidden');

    return (
      <Card key={field.id}>
        <CardContent className="p-4 space-y-4">
          {/* Field Header with Toggle */}
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3 flex-1">
              <div className={cn(
                "p-2 rounded-lg",
                isActive ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
              )}>
                <Icon className="w-4 h-4" />
              </div>

              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <Label className="font-medium">{field.label}</Label>
                  {field.recommended && (
                    <Badge variant="secondary" className="text-xs">
                      Recommended
                    </Badge>
                  )}
                  {field.alwaysShown && (
                    <Badge variant="default" className="text-xs">
                      Required
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {field.description}
                </p>
              </div>
            </div>

            {/* Toggle Switch - only show if not always shown */}
            {!field.alwaysShown && (
              <div className="flex items-center space-x-3 ml-4">
                <span className={cn(
                  "text-sm font-medium",
                  isActive ? 'text-primary' : 'text-muted-foreground'
                )}>
                  {toggleText}
                </span>
                <Switch
                  checked={isActive}
                  onCheckedChange={(checked) => field.onChange(checked)}
                />
              </div>
            )}
          </div>

          {/* Label & Placeholder Customization - show only if field is active and has labels */}
          {isActive && (field.hasLabel || field.hasPlaceholder) && (
            <>
              <Separator />
              <div className="space-y-3">
                {field.hasLabel && (
                  <div className="space-y-1.5">
                    <Label htmlFor={`${field.id}-label`} className="text-xs text-muted-foreground">
                      Field Label
                    </Label>
                    <Input
                      id={`${field.id}-label`}
                      value={fieldLabels[field.labelKey] || ''}
                      onChange={(e) => handleLabelChange(field.labelKey, e.target.value)}
                      placeholder={defaults[field.labelKey]}
                      className="h-9"
                    />
                  </div>
                )}
                {field.hasPlaceholder && (
                  <div className="space-y-1.5">
                    <Label htmlFor={`${field.id}-placeholder`} className="text-xs text-muted-foreground">
                      Placeholder Text
                    </Label>
                    <Input
                      id={`${field.id}-placeholder`}
                      value={fieldLabels[field.placeholderKey] || ''}
                      onChange={(e) => handleLabelChange(field.placeholderKey, e.target.value)}
                      placeholder={defaults[field.placeholderKey]}
                      className="h-9"
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className={cn("space-y-4", className)}>
      <div>
        <h3 className="text-sm font-medium mb-1">Form Configuration</h3>
        <p className="text-xs text-muted-foreground">
          Configure which fields appear in your booking form and customize their labels.
        </p>
      </div>

      <Accordion type="multiple" defaultValue={["standard-fields", "custom-questions"]} className="w-full">
        {/* Standard Fields Section */}
        <AccordionItem value="standard-fields">
          <AccordionTrigger className="text-sm font-medium">
            Standard Fields
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 pt-2">
              {standardFields.map(renderField)}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Custom Questions Section */}
        <AccordionItem value="custom-questions">
          <AccordionTrigger className="text-sm font-medium">
            Custom Questions
          </AccordionTrigger>
          <AccordionContent>
            <div className="pt-2">
              <CustomQuestionsTab
                questions={customQuestions}
                onChange={onCustomQuestionsChange}
              />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default FormFieldsTab;
